import api from './api';

/**
 * 調度員相關API服務
 */
export const dispatcherService = {
    /**
     * 獲取調度員列表
     * @param {Object} params - 查詢參數
     * @param {number} params.page - 頁碼
     * @param {number} params.limit - 每頁記錄數
     * @param {string} params.status - 狀態過濾
     * @param {number} params.fleet_id - 車隊ID過濾
     * @param {string} params.search - 搜尋關鍵字
     * @returns {Promise<{data: Array, meta: Object}>} 調度員列表和元數據
     */
    getDispatchers: async (params = {}) => {
        const response = await api.get('/dispatchers', { params });
        return response.data;
    },

    /**
     * 根據ID獲取調度員詳情
     * @param {number|string} dispatcherId - 調度員ID
     * @returns {Promise<Object>} 調度員詳情
     */
    getDispatcherById: async (dispatcherId) => {
        const response = await api.get(`/dispatchers/${dispatcherId}`);
        return response.data;
    },

    /**
     * 創建新調度員
     * @param {Object} dispatcherData - 調度員數據
     * @returns {Promise<Object>} 創建的調度員
     */
    createDispatcher: async (dispatcherData) => {
        const response = await api.post('/dispatchers', dispatcherData);
        return response.data;
    },

    /**
     * 更新調度員信息
     * @param {number|string} dispatcherId - 調度員ID
     * @param {Object} dispatcherData - 調度員數據
     * @returns {Promise<Object>} 更新後的調度員
     */
    updateDispatcher: async (dispatcherId, dispatcherData) => {
        const response = await api.put(`/dispatchers/${dispatcherId}`, dispatcherData);
        return response.data;
    },

    /**
     * 刪除調度員
     * @param {number|string} dispatcherId - 調度員ID
     * @returns {Promise<void>}
     */
    deleteDispatcher: async (dispatcherId) => {
        await api.delete(`/dispatchers/${dispatcherId}`);
    },

    /**
     * 獲取調度員統計數據
     * @param {number|string} dispatcherId - 調度員ID
     * @returns {Promise<Object>} 調度員統計數據
     */
    getDispatcherStats: async (dispatcherId) => {
        const response = await api.get(`/dispatchers/${dispatcherId}/stats`);
        return response.data;
    },

    /**
     * 獲取調度員處理的訂單
     * @param {number|string} dispatcherId - 調度員ID
     * @param {Object} params - 查詢參數
     * @returns {Promise<{data: Array, meta: Object}>} 訂單列表和元數據
     */
    getDispatcherOrders: async (dispatcherId, params = {}) => {
        const response = await api.get(`/dispatchers/${dispatcherId}/orders`, { params });
        return response.data;
    },

    /**
     * 重置調度員密碼
     * @param {number|string} dispatcherId - 調度員ID
     * @returns {Promise<{password: string}>} 新密碼
     */
    resetPassword: async (dispatcherId) => {
        const response = await api.post(`/dispatchers/${dispatcherId}/reset-password`);
        return response.data;
    },

    /**
     * 更新調度員權限
     * @param {number|string} dispatcherId - 調度員ID
     * @param {Object} permissions - 權限設置
     * @returns {Promise<Object>} 更新後的調度員
     */
    updatePermissions: async (dispatcherId, permissions) => {
        const response = await api.patch(`/dispatchers/${dispatcherId}/permissions`, permissions);
        return response.data;
    },

    /**
     * 設置調度員工作狀態
     * @param {number|string} dispatcherId - 調度員ID
     * @param {string} status - 狀態
     * @returns {Promise<Object>} 更新後的調度員
     */
    setStatus: async (dispatcherId, status) => {
        const response = await api.patch(`/dispatchers/${dispatcherId}/status`, { status });
        return response.data;
    }
};

export default dispatcherService;